// The visual language of the cockpit prototype, kept deliberately:
// warm off-white ground, value colour as a left border, IBM Plex Mono for
// metadata, Sora for text. Not a generic dashboard.

export const C = {
  ground: "#FAF8F4",
  card: "#FFFFFF",
  cardQuiet: "#FDFCF9",
  ink: "#1E1F24",
  inkSoft: "#5F5B52",
  inkFaint: "#8B877E",
  inkGhost: "#B0ACA2",
  rule: "#E8E4DB",
  ruleStrong: "#DAD5C9",
  flagBg: "#F6F1E4",
  flagInk: "#9A8A55",
  alert: "#9C4A3C",
  good: "#3E8E6B",
};

export const mono = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
export const sans = "'Sora', system-ui, -apple-system, Segoe UI, sans-serif";

export const tierChip = (tier: string | null) => {
  if (tier === "must") return { bg: "#F3E8E5", fg: "#9C4A3C" };
  if (tier === "should") return { bg: "#EDEAE2", fg: "#6B6759" };
  if (tier === "nice") return { bg: "#EAEEF3", fg: "#5A6673" };
  return { bg: "#F0EEE9", fg: "#8B877E" };
};

// ─────────────────────────────────────────────────────────────────────────
// Session 16 — the token layer.
//
// Everything above this line predates the session and is untouched. Below is
// the rest of what "a shared style layer" needs: a type scale, a spacing
// scale, border weights, radius, icon sizes and a minimum touch target — real
// named values, in the one place `presentation.ts` is for behaviour.
//
// These were derived from what Today, Week, Pantry, the tab bar, the menu and
// Detail actually used, not invented ahead of need. Where two call sites used
// near-identical values that plainly meant the same thing (10 vs 10.5, 11 vs
// 11.5, 13.5 vs 14, 17 vs 18) they were folded into one step here — each fold
// is listed in session 16's completion report as a forced visible change.
// Where two values were both genuinely load-bearing at different weights
// (12 next to 11, 13 next to 14) they were kept as separate steps rather than
// forced together.
// ─────────────────────────────────────────────────────────────────────────

/** The one colour spent on "this is active / primary / selected". Not a new
 * hex — `C.ink` was already this everywhere it mattered (the current tab, the
 * primary button, a bold menu label); this just gives that role a name so a
 * component reaches for what it means rather than for `C.ink` and hoping the
 * reader infers the intent. */
export const accent = C.ink;

/**
 * The one place a task's row/sheet accent colour is resolved. Ten values,
 * each carrying its own colour from the server; every row reads it through
 * here rather than writing `t.value_colour ?? C.ruleStrong` at its own call
 * site. Appearance is unchanged — this still falls back to the neutral rule
 * colour when a task carries none — but the policy behind it is now one
 * function. Swapping per-value colour for a single shared accent later is one
 * edit here, not a hunt through every card.
 */
export const valueAccent = (colour: string | null) => colour ?? C.ruleStrong;

/** Font sizes. Every text literal in the refactored surfaces resolves to one
 * of these. */
export const type = {
  /** Pantry's "not sent yet" / refused tags; Future's per-day and per-group
   * sub-labels. Was 10 in the latter two — see the report. */
  micro: 10.5,
  /** The mono uppercase eyebrow (section headers), compact meta lines, chips,
   * pills, and the tab bar / menu "more" label. The tab bar was 11.5 — see
   * the report. */
  small: 11,
  /** A meta line carrying more weight than a list row's: the heavy card's
   * next step, Detail's due/mode line, the waiting/chase line. */
  meta: 12,
  /** Secondary prose: why-today, flag body, notes, why-chain, empty states. */
  body: 13,
  /** Primary single-line text: row titles, the quiet-hour card, button
   * labels, the sheet's NEXT text and its date field. Pantry's item name was
   * 13.5 — see the report. */
  bodyLarge: 14,
  /** The day's one open-ended line, and the chrome's full-width buttons. */
  lead: 15,
  /** Text inputs only — below 16px iOS zooms the page on focus. */
  input: 16,
  /** The heavy card's title and the detail sheet's title. The sheet was
   * 18 — see the report. */
  titleLarge: 17,
  /** The date heading, once per screen. */
  display: 21,
} as const;

/** Spacing. Composed into padding/margin/gap shorthand at each call site,
 * the way the values already were — this names the atoms rather than
 * collapsing every composite into a single new shorthand, which would have
 * reflowed far more layout than the session asked for. */
export const space = {
  xxs: 2,
  xs: 4,
  sm: 6,
  ms: 8,
  md: 10,
  ml: 12,
  lg: 14,
  xl: 16,
  xxl: 18,
  xxxl: 20,
  huge: 24,
  massive: 28,
} as const;

/** Corner radius. */
export const radius = {
  /** The pantry tick / checkbox square. */
  sm: 4,
  /** Small square controls: the Future view's scroll-nudge buttons. */
  md: 8,
  /** Form inputs, and primary/secondary buttons. */
  lg: 10,
  /** Cards: a task row, the quiet-hour card, the flag card, the travel card,
   * the prompt block. */
  xl: 12,
  /** Chips and pills — tier, mode. */
  pill: 99,
  /** A bottom sheet's top corners — the menu and the detail sheet. */
  sheetTop: "16px 16px 0 0",
} as const;

/** Border weights. */
export const border = {
  /** The `C.rule` / `C.ruleStrong` outline every card and rule line uses. */
  hairline: 1,
  /** The tab bar's "this one is open" underline. */
  active: 2,
  /** The value-colour accent: a row's left border, the detail sheet's top
   * border. */
  accent: 4,
} as const;

/** The bottom sheet's backdrop — `C.ink` at 35%, the same literal the menu
 * and the detail sheet already both used. */
export const scrim = "rgba(30,31,36,0.35)";

/** Fixed-size controls that are not text. */
export const icon = {
  /** The pantry tick / routine checkbox. */
  tick: 15,
  /** The Future view's round scroll-nudge buttons. */
  button: 32,
} as const;

/** The minimum side of a tappable control, per platform accessibility
 * guidance. Recorded here as a reference value for new controls — it is not
 * retrofitted onto existing ones in this session, since several (the Future
 * view's 32px nudge buttons) are smaller and resizing them was not asked for
 * and was not forced by extracting a component. See the completion report. */
export const touch = {
  min: 44,
} as const;

/** 25 Aug — never "3 days overdue". */
export const shortDate = (iso: string | null) =>
  iso
    ? new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", timeZone: "UTC",
      })
    : "";

/**
 * 15 Aug 2027 — the year appears only when it is not this one. Without it a
 * domain renewal a year out reads as a fortnight away, which is the kind of
 * quiet wrongness that makes a money panel untrustworthy.
 */
export const dateMaybeYear = (iso: string | null) => {
  if (!iso) return "";
  const sameYear = iso.slice(0, 4) === todayISO().slice(0, 4);
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", timeZone: "UTC",
    ...(sameYear ? {} : { year: "numeric" }),
  });
};

export const longDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", timeZone: "UTC",
  });

export const todayISO = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());

export const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
